/**
 * What a section can be — read from the product table loaded for the current
 * retailer, never from a fixed list. If a retailer doesn't carry SlatWall, the
 * designer doesn't offer SlatWall; if they only carry it in white, white is the
 * only swatch.
 *
 * Colours are still limited to the ones the calculation engine knows
 * (`ProductColor`): a product in any other colour is ignored here until the
 * engine can carry arbitrary colours.
 */

import { Product, ProductColor } from '../../types';
import { SectionProductType } from '../../types/sections';
import { getProductsByType } from '../../data/productTable';

/** Product-table acronym for each section type. */
const ACRONYM: Record<SectionProductType, string> = { WCB: 'W&CB', SlatWall: 'SW' };

const KNOWN_COLORS: ProductColor[] = ['White', 'Gray'];

function activeProducts(type: SectionProductType): Product[] {
  return getProductsByType(ACRONYM[type]).filter((p) => p.active !== false);
}

/** Colours this retailer carries for a section type, in the engine's usual order. */
export function getAvailableColors(type: SectionProductType): ProductColor[] {
  const carried = new Set(activeProducts(type).map((p) => p.color));
  return KNOWN_COLORS.filter((c) => carried.has(c));
}

/**
 * Section types that can go on a surface: the retailer must carry the product,
 * and SlatWall never goes on a ceiling.
 */
export function getAvailableProductTypes(surfaceKind: 'wall' | 'ceiling'): SectionProductType[] {
  const types: SectionProductType[] = surfaceKind === 'ceiling' ? ['WCB'] : ['WCB', 'SlatWall'];
  return types.filter((t) => getAvailableColors(t).length > 0);
}

/** `preferred` if the retailer carries it for this type, else their first colour (null if none). */
export function pickColor(type: SectionProductType, preferred?: ProductColor): ProductColor | null {
  const colors = getAvailableColors(type);
  if (preferred && colors.includes(preferred)) return preferred;
  return colors[0] ?? null;
}

export const PRODUCT_TYPE_LABEL: Record<SectionProductType, string> = {
  // A product NAME, written exactly as Trusscore writes it: one word, no spaces.
  WCB: 'Wall&CeilingBoard',
  SlatWall: 'SlatWall',
};
